// Shapes for the in-app notification API (ENG-3380, ENG-3382).
//
// Hand-written rather than generated, matching `coachMessages.ts` and
// `coachAvailability.ts`: the generated client is not wired up for these routes.

/** Topic keys the bell knows how to word. */
export const NOTIFICATION_TOPIC_KEYS = [
  'coaching.message_received',
  'coaching.message_received_coach',
  'coaching.meeting_scheduled',
  'coaching.meeting_rescheduled',
  'coaching.meeting_cancelled',
  'coaching.meeting_reminder',
  'coaching.meeting_missed_coach',
  'coaching.meeting_missed_learner',
] as const

export type NotificationTopicKey = (typeof NOTIFICATION_TOPIC_KEYS)[number]

/**
 * What a notification is about, and therefore where clicking it goes.
 *
 * Mirrors `app/notifications/source_types.py`. The API sends the raw string, so
 * an unrecognised one is possible and is treated as "no destination" rather than
 * as an error.
 */
export const NOTIFICATION_SOURCE_TYPES = {
  coachMessage: 'coach_message',
  liveSession: 'live_session',
} as const

/**
 * One bell entry.
 *
 * Carries no rendered text and no link, deliberately. The wording is assembled
 * at display time from `topic_key` plus `payload`, so a notification written
 * three weeks ago appears in whatever language the reader uses now; and the
 * destination is derived from the source pair, so renaming a route is a change
 * here rather than a data migration.
 */
export type AppNotification = {
  readonly id: string
  readonly topic_key: string
  /** Allow-listed at write time, so it never carries message content. Empty for
   *  a kind nobody listed server-side. */
  readonly payload: NotificationPayload
  readonly source_type: string | null
  readonly source_id: string | null
  readonly created_at: string
  /** Null while unread. Only ever the reader's own state. */
  readonly read_at: string | null
}

/** The values a notification's wording interpolates, plus what it points at. */
export type NotificationPayload = {
  readonly coach_name?: string
  readonly learner_name?: string
  /** Which conversation, for a message notification. The coach's screen opens by
   *  learner, and the notification's own `source_id` is the message. */
  readonly learner_user_id?: string
  /** An instant, formatted at display time in `timezone`. */
  readonly starts_at?: string
  /** The IANA zone the notice's email states. Absent on rows written before it was kept. */
  readonly timezone?: string
}

/** A position in the list's ordering, for paging back through it. */
export type NotificationCursor = {
  readonly created_at: string
  readonly id: string
}

/** One page of notifications, newest first. */
export type NotificationPage = {
  readonly notifications: readonly AppNotification[]
  /** Null once the page has reached the oldest notification. */
  readonly next_cursor: NotificationCursor | null
}

/** The badge number. */
export type UnreadNotificationCount = {
  readonly unread: number
}
